
import { useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useShopAuth } from '@/contexts/ShopAuthContext';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { User, Store } from 'lucide-react';

const UnifiedLogin = () => {
  // Customer login state
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPassword, setCustomerPassword] = useState('');
  const [isCustomerLoading, setIsCustomerLoading] = useState(false);

  // Shop login state
  const [shopId, setShopId] = useState('');
  const [shopPassword, setShopPassword] = useState('');
  const [isShopLoading, setIsShopLoading] = useState(false);

  const { user, signIn } = useAuth();
  const { shop, login: shopLogin, loading: shopAuthLoading } = useShopAuth();
  const navigate = useNavigate();

  // Wait for auth contexts to load before checking redirect
  if (shopAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">جاري التحميل...</div>
      </div>
    );
  }

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/" replace />;
  }

  if (shop && shop.slug) {
    return <Navigate to={`/admin/${shop.slug}`} replace />;
  }

  const handleCustomerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCustomerLoading(true);

    try {
      const result = await signIn(customerEmail, customerPassword);
      
      if (result.error) {
        toast.error(result.error);
      } else {
        toast.success('تم تسجيل الدخول بنجاح');
        navigate('/');
      }
    } catch (error) {
      toast.error('فشل تسجيل الدخول. حاول مرة أخرى.');
    } finally {
      setIsCustomerLoading(false);
    }
  };

  const handleShopSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsShopLoading(true);

    try {
      const result = await shopLogin(shopId, shopPassword);
      
      if (result.error) {
        toast.error(result.error);
      } else {
        toast.success('تم تسجيل دخول الورشة بنجاح');
        // Navigate to the shop's specific admin page using the shopId
        navigate(`/admin/${shopId}`);
      }
    } catch (error) {
      toast.error('فشل تسجيل الدخول. حاول مرة أخرى.');
    } finally {
      setIsShopLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      
      <div className="flex-grow flex items-center justify-center pt-16 pb-8 px-4 sm:pt-20 sm:pb-12">
        <Card className="w-full max-w-md">
          <CardHeader className="px-4 pt-6 pb-4 sm:px-6">
            <CardTitle className="text-xl sm:text-2xl text-center text-autocare-blue">تسجيل الدخول</CardTitle>
            <CardDescription className="text-center text-sm sm:text-base">
              اختر نوع الحساب لتسجيل الدخول
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-6 sm:px-6">
            <Tabs defaultValue="customer" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="customer" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  عميل
                </TabsTrigger>
                <TabsTrigger value="shop" className="flex items-center gap-2">
                  <Store className="h-4 w-4" />
                  ورشة
                </TabsTrigger>
              </TabsList>

              <TabsContent value="customer">
                <form onSubmit={handleCustomerSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer-email">البريد الإلكتروني</Label>
                    <Input
                      id="customer-email"
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="أدخل بريدك الإلكتروني"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="customer-password">كلمة المرور</Label>
                    <Input
                      id="customer-password"
                      type="password"
                      value={customerPassword}
                      onChange={(e) => setCustomerPassword(e.target.value)}
                      placeholder="أدخل كلمة المرور"
                      required
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isCustomerLoading}
                  >
                    {isCustomerLoading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
                  </Button>
                </form>
                
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-600">
                    ليس لديك حساب؟{' '}
                    <Link to="/signup" className="text-autocare-blue hover:underline">
                      إنشاء حساب جديد
                    </Link>
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="shop">
                <form onSubmit={handleShopSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="shop-id">معرف الورشة</Label>
                    <Input
                      id="shop-id"
                      type="text"
                      value={shopId}
                      onChange={(e) => setShopId(e.target.value)}
                      placeholder="أدخل معرف الورشة"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="shop-password">كلمة المرور</Label>
                    <Input
                      id="shop-password"
                      type="password"
                      value={shopPassword}
                      onChange={(e) => setShopPassword(e.target.value)}
                      placeholder="أدخل كلمة المرور"
                      required
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isShopLoading}
                  >
                    {isShopLoading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="text-center">
                <Link 
                  to="/super-admin-login" 
                  className="text-sm text-gray-600 hover:text-autocare-blue transition-colors"
                >
                  Super Admin Access
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default UnifiedLogin;
